/**
 * Schreibwege der eigenen Listen (F-33): Listen anlegen (aus Vorlage),
 * Eintraege schnell erfassen, abhaken, umsortieren, in eine Aufgabe oder
 * einen gemerkten Gedanken umwandeln - und gemerkte Gedanken auf eine Liste
 * verschieben.
 */
import { db } from '@/db/schema';
import { createTask } from '@/db/repositories/tasks';
import { addThought, toggleMerken, updateThought } from '@/db/repositories/thoughts';
import { inDenPapierkorb, rueckgaengigMachbar } from '@/db/repositories/papierkorb';
import { AREA_COLORS } from '@/domain/areaColors';
import { gruppeFuer, haeufigMerken, linkNormal, mengeTrennen, orderZwischen, VORLAGEN } from '@/domain/listen/listen';
import type { Liste, ListenEintrag, ListenVorlage } from '@/domain/listen/typen';
import { nowInstant, today } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import type { ID } from '@/domain/types';

const FARBE_JE_VORLAGE: Record<ListenVorlage, number> = { einkauf: 4, ideen: 3, pack: 1, leer: 0 };

export async function listeAnlegen(vorlage: ListenVorlage, name?: string, areaId: ID | null = null): Promise<Liste> {
  const v = VORLAGEN[vorlage];
  const ts = nowInstant();
  const letzte = (await db.listen.orderBy('order').last())?.order ?? 0;
  const liste: Liste = {
    id: newId(),
    name: (name ?? '').trim() || v.name || 'Neue Liste',
    symbol: v.symbol,
    farbe: AREA_COLORS[FARBE_JE_VORLAGE[vorlage]]!.value,
    areaId,
    order: letzte + 1,
    archiviert: false,
    vorlage,
    gruppen: [...v.gruppen],
    zeigeErledigte: true,
    createdAt: ts,
    updatedAt: ts,
  };
  if (v.mitMenge) liste.mitMenge = true;
  if (v.mitBewertung) liste.mitBewertung = true;
  await db.listen.add(liste);
  return liste;
}

export type ListenAenderung = Partial<Pick<Liste, 'name' | 'symbol' | 'farbe' | 'areaId' | 'archiviert' | 'angeheftet' | 'mitMenge' | 'mitBewertung' | 'gruppen' | 'zeigeErledigte'>>;

export async function listeAendern(id: ID, patch: ListenAenderung): Promise<void> {
  const p = { ...patch };
  if (p.name !== undefined) p.name = p.name.trim() || 'Neue Liste';
  if (p.gruppen) p.gruppen = [...new Set(p.gruppen.map((g) => g.trim()).filter(Boolean))];
  await db.listen.update(id, { ...p, updatedAt: nowInstant() });
}

/** Liste eine Position nach oben (-1) oder unten (+1) unter den nicht archivierten. */
export async function listeVerschieben(id: ID, richtung: -1 | 1): Promise<void> {
  await db.transaction('rw', db.listen, async () => {
    const alle = (await db.listen.orderBy('order').toArray()).filter((l) => !l.archiviert);
    const i = alle.findIndex((l) => l.id === id);
    const j = i + richtung;
    if (i < 0 || j < 0 || j >= alle.length) return;
    const ts = nowInstant();
    await db.listen.update(alle[i]!.id, { order: alle[j]!.order, updatedAt: ts });
    await db.listen.update(alle[j]!.id, { order: alle[i]!.order, updatedAt: ts });
  });
}

export async function listeLoeschen(id: ID): Promise<void> {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'listen', zeileIds: [id] }, () => db.transaction('rw', db.listen, db.listenEintraege, async () => {
    await db.listenEintraege.where('listeId').equals(id).delete();
    await db.listen.delete(id);
  }));
}

async function amEnde(listeId: ID): Promise<number> {
  const letzte = await db.listenEintraege.where('[listeId+order]').between([listeId, -Infinity], [listeId, Infinity]).last();
  return (letzte?.order ?? 0) + 1;
}

/**
 * Schnellerfassung: Mengen („2× Milch“) und Abteilung werden erkannt,
 * der Eintrag kommt ans Ende seiner Gruppe und wird fuer Vorschlaege gemerkt.
 */
export async function eintragHinzufuegen(listeId: ID, eingabe: string, extra: Partial<Pick<ListenEintrag, 'menge' | 'gruppe' | 'notiz' | 'link'>> = {}): Promise<ListenEintrag | null> {
  return db.transaction('rw', db.listen, db.listenEintraege, async () => {
    const liste = await db.listen.get(listeId);
    if (!liste) return null;
    const getrennt = liste.mitMenge ? mengeTrennen(eingabe) : { text: eingabe.trim() };
    if (!getrennt.text) return null;
    const ts = nowInstant();
    const e: ListenEintrag = { id: newId(), listeId, text: getrennt.text, erledigt: 0, order: await amEnde(listeId), createdAt: ts, updatedAt: ts };
    const menge = extra.menge ?? getrennt.menge;
    if (menge) e.menge = menge;
    const gruppe = extra.gruppe ?? gruppeFuer(e.text, liste);
    if (gruppe) e.gruppe = gruppe;
    if (extra.notiz?.trim()) e.notiz = extra.notiz.trim();
    const link = extra.link ? linkNormal(extra.link) : undefined;
    if (link) e.link = link;
    await db.listenEintraege.add(e);
    await db.listen.update(listeId, { haeufig: haeufigMerken(liste.haeufig, e), updatedAt: ts });
    return e;
  });
}

export type EintragsAenderung = Partial<Pick<ListenEintrag, 'text' | 'notiz' | 'link' | 'menge' | 'gruppe' | 'bewertung'>>;

export async function eintragAendern(id: ID, patch: EintragsAenderung): Promise<void> {
  const alt = await db.listenEintraege.get(id);
  if (!alt) return;
  const neu: ListenEintrag = { ...alt, updatedAt: nowInstant() };
  if (patch.text !== undefined && patch.text.trim()) neu.text = patch.text.trim();
  for (const k of ['notiz', 'menge', 'gruppe'] as const) {
    if (k in patch) {
      const v = patch[k]?.trim();
      if (v) neu[k] = v; else delete neu[k];
    }
  }
  if ('link' in patch) {
    const l = patch.link ? linkNormal(patch.link) : undefined;
    if (l) neu.link = l; else delete neu.link;
  }
  if ('bewertung' in patch) {
    const b = patch.bewertung;
    if (typeof b === 'number' && Number.isInteger(b) && b >= 1 && b <= 10) neu.bewertung = b; else delete neu.bewertung;
  }
  await db.listenEintraege.put(neu);
}

export async function abhaken(id: ID, erledigt: boolean): Promise<void> {
  const ts = nowInstant();
  const e = await db.listenEintraege.get(id);
  if (!e) return;
  const neu: ListenEintrag = { ...e, erledigt: erledigt ? 1 : 0, updatedAt: ts };
  if (erledigt) neu.erledigtAm = ts; else delete neu.erledigtAm;
  // Wieder offen: ans Ende seiner Gruppe statt mitten hinein.
  if (!erledigt) neu.order = await amEnde(e.listeId);
  await db.listenEintraege.put(neu);
}

/** Per Ziehen: zwischen zwei Nachbarn einsortieren, ggf. in eine andere Gruppe. */
export async function eintragEinsortieren(id: ID, vorherId: ID | null, nachherId: ID | null, gruppe: string | null): Promise<void> {
  await db.transaction('rw', db.listenEintraege, async () => {
    const [vorher, nachher] = await Promise.all([
      vorherId ? db.listenEintraege.get(vorherId) : undefined,
      nachherId ? db.listenEintraege.get(nachherId) : undefined,
    ]);
    const e = await db.listenEintraege.get(id);
    if (!e) return;
    const neu: ListenEintrag = { ...e, order: orderZwischen(vorher?.order, nachher?.order), updatedAt: nowInstant() };
    if (gruppe) neu.gruppe = gruppe; else delete neu.gruppe;
    await db.listenEintraege.put(neu);
  });
}

export const eintragLoeschen = (id: ID): Promise<void> =>
  inDenPapierkorb({ art: 'geloescht', tabelle: 'listenEintraege', zeileIds: [id] }, () => db.listenEintraege.delete(id));

/** Alle abgehakten Eintraege einer Liste - als EIN Papierkorb-Eintrag (F-45). */
export async function abgehakteEntfernen(listeId: ID): Promise<number> {
  const ids = await db.listenEintraege.where('listeId').equals(listeId).filter((e) => e.erledigt === 1).primaryKeys();
  if (!ids.length) return 0;
  const liste = await db.listen.get(listeId);
  const titel = ids.length > 1 ? `${ids.length} abgehakte aus „${liste?.name ?? 'Liste'}“` : undefined;
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'listenEintraege', zeileIds: ids, titel }, () => db.listenEintraege.bulkDelete(ids));
  return ids.length;
}

/** Wiederkehrende Listen (Packliste): alles wieder offen, Reihenfolge bleibt. */
export async function alleZuruecksetzen(listeId: ID): Promise<void> {
  const ts = nowInstant();
  await db.listenEintraege.where('listeId').equals(listeId).filter((e) => e.erledigt === 1)
    .modify((e) => { e.erledigt = 0; delete e.erledigtAm; e.updatedAt = ts; });
}

/** Braucht doch einen Termin: wird eine Aufgabe (in der Inbox, mit dem Bereich der Liste). Rueckgaengig machbar (F-45). */
export const inAufgabe = (id: ID): Promise<ID | null> => rueckgaengigMachbar({ art: 'umgewandelt', tabelle: 'listenEintraege', zeileIds: [id] }, async () => {
  const e = await db.listenEintraege.get(id);
  if (!e) return null;
  const liste = await db.listen.get(e.listeId);
  const notiz = [e.menge ? `Menge: ${e.menge}` : '', e.notiz ?? '', e.link ?? '', liste ? `Aus der Liste „${liste.name}“` : ''].filter(Boolean).join('\n\n');
  const task = await createTask({ title: e.text, notes: notiz || undefined, status: 'inbox', areaId: liste?.areaId ?? null });
  await db.listenEintraege.delete(id);
  return task.id;
});

/** Doch zum Weiterdenken: wird ein gemerkter Gedanke. Rueckgaengig machbar (F-45). */
export const alsGedanke = (id: ID): Promise<ID | null> => rueckgaengigMachbar({ art: 'umgewandelt', tabelle: 'listenEintraege', zeileIds: [id] }, async () => {
  const e = await db.listenEintraege.get(id);
  if (!e) return null;
  const g = await addThought(today(), e.text);
  if (!g) return null;
  await toggleMerken(g.id);
  const notiz = [e.notiz ?? '', e.link ?? ''].filter(Boolean).join('\n');
  if (notiz) await updateThought(g.id, { note: notiz });
  await db.listenEintraege.delete(id);
  return g.id;
});

/** Gemerkten Gedanken auf eine Liste verschieben (F-33). Rueckgaengig machbar (F-45). */
export const gedankeAufListe = (gedankeId: ID, listeId: ID): Promise<ID | null> => rueckgaengigMachbar({ art: 'verschoben', tabelle: 'thoughts', zeileIds: [gedankeId] }, async () => {
  const g = await db.thoughts.get(gedankeId);
  if (!g) return null;
  const e = await eintragHinzufuegen(listeId, g.text, g.note ? { notiz: g.note } : {});
  if (!e) return null;
  await db.thoughts.delete(gedankeId);
  return e.id;
});
